import { useEffect, useState } from "react";
import axios from "axios";
import {
  PieChart, Pie, Cell, BarChart, Bar,
  Tooltip, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import DashboardLayout from "../layout/DashboardLayout";
import "../layout/Dashboard.css";
import "./GuestAnalyticsPage.css";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

interface Guest {
  id: string;
  name: string;
  phone: string;
  loyaltyPoints: number;
  isVip: boolean;
  source: string;
}

interface BySource {
  source: string;
  count: number;
  [key: string]: unknown;
}

interface GuestAnalyticsData {
  totalGuests: number;
  vipGuests: number;
  newThisMonth: number;
  birthdaysThisMonth: number;
  bySource: BySource[];
  topGuests: Guest[];
}


const PIE_COLORS = ["#6366f1", "#10b981", "#f59e0b", "#ec4899", "#3b82f6", "#8b5cf6"];

const SOURCE_LABELS: Record<string, string> = {
  manual: "Manual",
  booking: "Booking.com",
  whatsapp: "WhatsApp",
  instagram: "Instagram",
  direct: "Direct",
  other: "Other",
};

const CustomPieTooltip = ({ active, payload }: any) => {
  if (active && payload?.length) {
    return (
      <div className="ga-tooltip">
        <p className="ga-tooltip-label">{SOURCE_LABELS[payload[0].name] ?? payload[0].name}</p>
        <p className="ga-tooltip-value">{payload[0].value} guests</p>
      </div>
    );
  }
  return null;
};

const CustomBarTooltip = ({ active, payload, label }: any) => {
  if (active && payload?.length) {
    return (
      <div className="ga-tooltip">
        <p className="ga-tooltip-label">{label}</p>
        <p className="ga-tooltip-value">{payload[0].value} pts</p>
      </div>
    );
  }
  return null;
};

function StatCard({ label, value, accent }: { label: string; value: number | string; accent?: string }) {
  return (
    <div className="ga-stat-card">
      <p className="ga-stat-label">{label}</p>
      <p className="ga-stat-value" style={accent ? { color: accent } : undefined}>{value}</p>
    </div>
  );
}

export default function GuestAnalyticsPage() {
  const [data, setData] = useState<GuestAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/dashboard/guest-analytics`)
      .then((res) => setData(res.data))
      .catch(() => setError("Failed to load guest analytics"))
      .finally(() => setLoading(false));
  }, []);

  const top10 = data?.topGuests.slice(0, 10) ?? [];
  const barData = top10.map((g) => ({ name: g.name.split(" ")[0], points: g.loyaltyPoints }));

  return (
    <DashboardLayout>
      <div className="ga-page">
        {/* Header */}
        <div className="ga-header">
          <div>
            <h1 className="ga-title">Guest Analytics</h1>
            <p className="ga-subtitle">Guest insights, loyalty, and source breakdown</p>
          </div>
        </div>

        {loading && <div className="ga-loading">Loading guest analytics…</div>}
        {error && <div className="ga-error">{error}</div>}

        {!loading && !error && data && (
          <>
            {/* Stat Cards */}
            <div className="ga-stats-grid">
              <StatCard label="Total Guests" value={data.totalGuests} />
              <StatCard label="VIP Guests" value={data.vipGuests} accent="#fbbf24" />
              <StatCard label="New This Month" value={data.newThisMonth} accent="#34d399" />
              <StatCard label="Birthdays This Month" value={data.birthdaysThisMonth} accent="#f472b6" />
            </div>

            {/* Charts */}
            <div className="ga-charts-row">
              {/* Pie Chart - by Source */}
              <div className="ga-chart-card">
                <h2 className="ga-chart-title">Guests by Source</h2>
                <p className="ga-chart-sub">Booking channel distribution</p>
                {data.bySource?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={data.bySource}
                        dataKey="count"
                        nameKey="source"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        innerRadius={44}
                        paddingAngle={3}
                      >
                        {data.bySource.map((_, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomPieTooltip />} />
                      <Legend
                        formatter={(val) => (
                          <span style={{ color: "rgba(242,235,217,0.3)", fontSize: 12 }}>
                            {SOURCE_LABELS[val] ?? val}
                          </span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="ga-empty-chart">No source data</div>
                )}
              </div>

              {/* Bar Chart - top guests by loyalty */}
              <div className="ga-chart-card">
                <h2 className="ga-chart-title">Top Guests by Loyalty Points</h2>
                <p className="ga-chart-sub">Top 10 most loyal guests</p>
                {barData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart
                      data={barData}
                      layout="vertical"
                      margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(99,102,241,0.08)" horizontal={false} />
                      <XAxis
                        type="number"
                        tick={{ fill: "#475569", fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        tick={{ fill: "#94a3b8", fontSize: 11 }}
                        axisLine={false}
                        tickLine={false}
                        width={70}
                      />
                      <Tooltip content={<CustomBarTooltip />} />
                      <Bar dataKey="points" fill="#6366f1" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="ga-empty-chart">No guest data</div>
                )}
              </div>
            </div>

            {/* Top Guests Table */}
            <div className="ga-table-card">
              <h2 className="ga-section-title">Top Guests</h2>
              {data.topGuests.length === 0 ? (
                <div className="ga-empty">No guests found</div>
              ) : (
                <div className="ga-table-wrap">
                  <table className="ga-table">
                    <thead>
                      <tr>
                        <th className="ga-th">#</th>
                        <th className="ga-th">Name</th>
                        <th className="ga-th">Phone</th>
                        <th className="ga-th">Loyalty Points</th>
                        <th className="ga-th">VIP</th>
                        <th className="ga-th">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.topGuests.map((g, i) => (
                        <tr key={g.id} className="ga-tr">
                          <td className="ga-td ga-td-rank">{i + 1}</td>
                          <td className="ga-td">
                            <div className="ga-guest-cell">
                              <div className="ga-avatar">{g.name?.[0] ?? "?"}</div>
                              <span className="ga-guest-name">{g.name}</span>
                            </div>
                          </td>
                          <td className="ga-td ga-td-muted">{g.phone || "—"}</td>
                          <td className="ga-td ga-td-points">{(g.loyaltyPoints ?? 0).toLocaleString()}</td>
                          <td className="ga-td">
                            {g.isVip ? (
                              <span className="ga-vip-badge">VIP</span>
                            ) : (
                              <span className="ga-td-muted">—</span>
                            )}
                          </td>
                          <td className="ga-td">
                            <span className="ga-source-badge">{SOURCE_LABELS[g.source] ?? g.source ?? "—"}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
